import React, { useCallback, useMemo } from 'react';
import { InputProps } from '../../Inputs/types/Input.types';
import { useTypedDispatch } from '../../../Hooks/useTypedDispatch';
import { useTypedSelector } from '../../../Hooks/useTypedSelector';
import { setValidationErrors } from '../../../Actions/Checkout.action';
import Input from '../../Inputs/Input';
import clsx from 'clsx';
import { translate } from '../../../Services/translation';
import { CHECKOUT_DROPSHIP_PRIVATE } from '../../../constants';

interface CustomerInfoInputProps {
  validationSchema: any;
  stateKey: string;
  id: string;
  onCustomerDetailsChange?: (
    stateKey: string,
    id: string,
    value: string
  ) => void;
}

const CheckoutCustomerInfoInput: React.FC<
  CustomerInfoInputProps &
    InputProps &
    React.InputHTMLAttributes<HTMLInputElement>
> = ({
  validationSchema,
  stateKey,
  onCustomerDetailsChange,
  id,
  wrapperClassName,
  ...props
}) => {
  const dispatch = useTypedDispatch();
  const { validationErrors = {}, payload } = useTypedSelector(
    (state) => state.checkout
  );
  const {
    isLoadingCustomerDetails,
    selectedDropshipAddressType = CHECKOUT_DROPSHIP_PRIVATE,
    useDropshipping,
  } = payload;

  const errorMessage = useMemo(
    () =>
      validationErrors[`${stateKey}-${id}`]
        ? validationErrors[`${stateKey}-${id}`][0]
        : null,
    [id, stateKey, validationErrors]
  );

  const validateOnBlur = useCallback(
    async (schema, stateKey, id, value) => {
      const errorKey = `${stateKey}-${id}`;
      try {
        await schema.validateAt(id, {
          ...payload[stateKey],
          country: payload.customerDetails.country,
          [id]: value,
          selectedDropshipAddressType,
          useDropshipping,
        });
        const validationErrorsCopy = { ...validationErrors };
        delete validationErrorsCopy[errorKey];
        dispatch(setValidationErrors(validationErrorsCopy));
      } catch (err) {
        dispatch(
          setValidationErrors({
            ...validationErrors,
            [errorKey]: err.errors,
          })
        );
      }
    },
    [dispatch, validationErrors]
  );

  return (
    <div className={clsx(wrapperClassName, 'relative mb-4')}>
      <Input
        {...props}
        wrapperClassName={wrapperClassName}
        className="text-lg"
        id={id}
        label={translate(`checkout.customerinfo.${id.toLowerCase()}`)}
        type="text"
        inputState={errorMessage ? 'error' : 'default'}
        message={errorMessage}
        onChange={(event) =>
          onCustomerDetailsChange &&
          onCustomerDetailsChange(stateKey, id, event.target.value)
        }
        onBlur={(e) =>
          validateOnBlur(validationSchema, stateKey, id, e.target.value)
        }
        inputSize="large"
        value={(payload[stateKey] && payload[stateKey][id]) ?? ''}
        showAnimatedLabel={true}
        disabled={isLoadingCustomerDetails}
      />
    </div>
  );
};

export default CheckoutCustomerInfoInput;
